import React, { useCallback, useState } from 'react';

import { Space } from 'antd-v5';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'scouting-ui/lib';

import { useStorePackRosterFilter } from '@modules/advancement/packRoster/duck/hooks';
import {
  fieldsVisibilitySel,
  filterSel,
  showSubUnitsSel,
  sorterSel,
} from '@modules/advancement/packRoster/duck/selectorsTyped';
import {
  setPackRosterFilter,
  setPackRosterSorter,
  setShowSubUnits,
  setSubUnitFilterTags,
} from '@modules/advancement/packRoster/duck/slice';
import type {
  PackRosterFilters,
  PackRosterSorter,
  SubUnitFilterTag,
} from '@modules/advancement/packRoster/types';
import { clearStoredPackRosterFilters } from '@modules/advancement/packRoster/utilsTyped';
import { TableFilterCard } from '@shared/components';
import {
  isMobileSel,
  organizationGuidSel,
} from '@shared/duck/selectors/general.selectors';

import {
  approvedSubUnitsSel,
  subUnitTypeSel,
} from '../../../../subUnits/duck/selectors';
import {
  isSubUnitAllowedSel,
  setFieldsVisibility,
  subUnitTypeNameSel,
  subUnitsFilteredTagsSel,
} from '../../../duck';
import {
  FilterSection,
  SortSection,
  SubUnitSection,
  VisibilitySection,
} from './Sections';

const { Collapse, Panel } = TableFilterCard;

interface FieldsVisibility {
  [key: string]: boolean;
}

interface Props {}

const PackRosterFilterCard: React.FC<React.PropsWithChildren<Props>> = ({
  children,
}) => {
  const dispatch = useDispatch();

  // Select from store
  const isMobile = useSelector(isMobileSel);
  const initialFilter = useSelector(filterSel);
  const initialSorter = useSelector(sorterSel);
  const initialFieldsVisibility = useSelector(fieldsVisibilitySel);
  const initialShowSubUnits = useSelector(showSubUnitsSel);
  const subUnits = useSelector(approvedSubUnitsSel);
  const isSubUnitAllowed = useSelector(isSubUnitAllowedSel);
  const subUnitTypeName = useSelector(subUnitTypeNameSel);
  const subUnitType = useSelector(subUnitTypeSel);
  const orgGuid = useSelector(organizationGuidSel);
  const initialTags = useSelector(subUnitsFilteredTagsSel);

  const storePackRosterFilter = useStorePackRosterFilter();

  // Local state
  const [filter, setFilter] = useState<PackRosterFilters>(initialFilter);
  const [sorter, setSorter] = useState<PackRosterSorter>(initialSorter);
  const [fieldsVisibility, setFieldsVisibilityState] =
    useState<FieldsVisibility>(initialFieldsVisibility);
  const [showSubUnits, setShowSubUnitsState] =
    useState<boolean>(initialShowSubUnits);
  const [activePanel, setActivePanel] = useState<string>('filter');
  const [subUnitsFilterTags, setSubUnitsFilterTagsState] =
    useState<SubUnitFilterTag[]>(initialTags);

  const handleActivePanelChange = useCallback((panel: string | string[]) => {
    setActivePanel(typeof panel === 'string' ? panel : panel[0]);
  }, []);

  const handleFilterChange = useCallback((newFilter: PackRosterFilters) => {
    setFilter(newFilter);
  }, []);

  const handleSorterChange = useCallback((newSorter: PackRosterSorter) => {
    setSorter(newSorter);
  }, []);

  const handleFieldsVisibilityChange = useCallback(
    (newFields: FieldsVisibility) => {
      setFieldsVisibilityState(newFields);
    },
    [],
  );

  const handleSubUnitChange = useCallback(() => {
    setShowSubUnitsState(prev => !prev);
  }, []);

  const resetState = useCallback(() => {
    setFilter(initialFilter);
    setSorter(initialSorter);
    setFieldsVisibilityState(initialFieldsVisibility);
    setShowSubUnitsState(initialShowSubUnits);
    setSubUnitsFilterTagsState(initialTags);
  }, [
    initialFilter,
    initialSorter,
    initialFieldsVisibility,
    initialShowSubUnits,
    initialTags,
  ]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) resetState();
    },
    [resetState],
  );

  const onSubUnitFilterChange = useCallback((id: string | number) => {
    const parsedId = typeof id === 'string' ? parseInt(id, 10) : id;
    setSubUnitsFilterTagsState(prev =>
      prev.map(tag =>
        tag.id === parsedId ? { ...tag, status: !tag.status } : tag,
      ),
    );
  }, []);

  const onSubUnitSelectAllChange = useCallback((selectAll: boolean) => {
    setSubUnitsFilterTagsState(prev =>
      prev.map(tag => ({ ...tag, status: selectAll })),
    );
  }, []);

  const handleSubmit = useCallback(() => {
    if (initialFilter !== filter) {
      dispatch(setPackRosterFilter({ filter }));
    }
    dispatch(setShowSubUnits({ show: showSubUnits }));
    dispatch(setSubUnitFilterTags({ tags: subUnitsFilterTags }));

    if (isMobile) {
      dispatch(setPackRosterSorter({ sorter }));
      dispatch(setFieldsVisibility(fieldsVisibility));
    }
    storePackRosterFilter({
      filter,
      showSubUnits,
      sorter,
      subUnitFilterTags: subUnitsFilterTags,
    });
  }, [
    dispatch,
    filter,
    sorter,
    fieldsVisibility,
    showSubUnits,
    subUnitsFilterTags,
    isMobile,
    initialFilter,
    storePackRosterFilter,
  ]);

  const renderFilterContent = useCallback(() => {
    const hasOrgSubUnits = Array.isArray(subUnits) && subUnits.length > 0;

    return (
      <Collapse activeKey={activePanel} onChange={handleActivePanelChange}>
        <Panel
          key="filter"
          header={
            <Space>
              <FormattedMessage id="packRoster.PackRosterFilterCard.showRoster" />
            </Space>
          }
        >
          <FilterSection values={filter} onChange={handleFilterChange} />
        </Panel>
        <Panel
          key="sorter"
          header={<FormattedMessage id="shared.sortBy" />}
          mobileOnly
        >
          <SortSection sorter={sorter} onChange={handleSorterChange} />
        </Panel>
        {!!isSubUnitAllowed && hasOrgSubUnits && (
          <Panel key="dens" header={<FormattedMessage id="shared.display" />}>
            <SubUnitSection
              subUnitTypeName={subUnitTypeName}
              disabled={!hasOrgSubUnits}
              selected={showSubUnits}
              onChange={handleSubUnitChange}
              subUnitsFilterTags={subUnitsFilterTags}
              onChangeSubUnits={onSubUnitFilterChange}
              onChangeSelectAllSubUnits={onSubUnitSelectAllChange}
            />
          </Panel>
        )}
        <Panel
          key="visiblity"
          header={<FormattedMessage id="shared.visibility" />}
          mobileOnly
        >
          <VisibilitySection
            subUnitType={subUnitType}
            isSubUnitAllowed={isSubUnitAllowed}
            values={fieldsVisibility}
            onChange={handleFieldsVisibilityChange}
          />
        </Panel>
      </Collapse>
    );
  }, [
    activePanel,
    filter,
    sorter,
    showSubUnits,
    fieldsVisibility,
    subUnitsFilterTags,
    subUnits,
    isSubUnitAllowed,
    subUnitType,
    subUnitTypeName,
    handleActivePanelChange,
    handleFilterChange,
    handleSorterChange,
    handleSubUnitChange,
    onSubUnitFilterChange,
    onSubUnitSelectAllChange,
    handleFieldsVisibilityChange,
  ]);

  const handleSelectClearSubFilters = () => {
    clearStoredPackRosterFilters(orgGuid);
    window.location.reload();
  };

  return (
    <TableFilterCard
      topLeftAction={
        <>
          <Button
            variant="link"
            color="white"
            onClick={handleSelectClearSubFilters}
          >
            <FormattedMessage id="packRoster.PackRosterFilterCard.resetFilters" />
          </Button>
        </>
      }
      extraActions={
        <>
          <Button type="text" danger onClick={handleSelectClearSubFilters}>
            <FormattedMessage id="packRoster.PackRosterFilterCard.resetFilters" />
          </Button>
        </>
      }
      content={renderFilterContent()}
      onOpenChange={handleOpenChange}
      onSubmit={handleSubmit}
    >
      {children as React.ReactElement}
    </TableFilterCard>
  );
};

export default PackRosterFilterCard;
